import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { optOutBackofficePostHog } from '../analytics';
import { Capacitor } from '@capacitor/core';

type ConsentStatus = 'granted' | 'denied' | 'unknown';

interface ConsentContextValue {
  consent: ConsentStatus;
  isDecided: boolean;
  grantConsent: () => void;
  denyConsent: () => void;
}

const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

const CONSENT_STORAGE_KEY = 'analytics_consent';

const readStoredConsent = (): ConsentStatus => {
  try {
    const saved = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (saved === 'granted' || saved === 'denied') {
      return saved;
    }
  } catch {
    // ignore storage errors
  }
  return 'unknown';
};

export const ConsentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Lecture synchrone : évite un flash du bandeau (E2E : clic « Tout refuser » qui timeout sous charge).
  const [consent, setConsent] = useState<ConsentStatus>(() => readStoredConsent());

  // Désactiver automatiquement le consentement sur iOS natif
  const isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';

  useEffect(() => {
    if (isNativeIOS) {
      // Sur iOS natif, on refuse automatiquement le tracking
      setConsent('denied');
      return;
    }

    const saved = readStoredConsent();
    if (saved !== 'unknown') {
      setConsent(saved);
    }
  }, [isNativeIOS]);

  const saveConsent = useCallback((status: Exclude<ConsentStatus, 'unknown'>) => {
    // Sur iOS natif, toujours refuser
    if (isNativeIOS) {
      setConsent('denied');
      return;
    }

    setConsent(status);
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, status);
    } catch {
      // ignore storage errors
    }
  }, [isNativeIOS]);

  const grantConsent = useCallback(() => saveConsent('granted'), [saveConsent]);
  const denyConsent = useCallback(() => saveConsent('denied'), [saveConsent]);

  const value = useMemo<ConsentContextValue>(() => ({
    consent,
    isDecided: consent !== 'unknown',
    grantConsent,
    denyConsent,
  }), [consent, grantConsent, denyConsent]);

  // Ensure tracking state is aligned when user denies consent
  useEffect(() => {
    if (consent === 'denied' && !isNativeIOS) {
      optOutBackofficePostHog();
    }
  }, [consent, isNativeIOS]);

  return (
    <ConsentContext.Provider value={value}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = (): ConsentContextValue => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return ctx;
};


